/**
 * BookingContext — holds the in-progress booking as the user moves through the funnel:
 * search criteria → selected flight → passengers/contact → fare → confirmed booking.
 *
 * State is mirrored to sessionStorage so a mid-flow refresh doesn't lose progress.
 * Route guards (see components/booking/RequireBookingStep) read this to block deep-links
 * into steps whose prerequisites aren't met.
 */
import React, { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "falcon_booking_flow";

/**
 * @typedef {Object} BookingState
 * @property {import('../mock/types').FlightSearchPayload | null} searchCriteria
 * @property {import('../mock/types').Flight | null} selectedFlight
 * @property {import('../mock/types').Passenger[]} passengers
 * @property {import('../mock/types').ContactInfo | null} contact
 * @property {import('../mock/types').FareBreakdown | null} fare
 * @property {import('../mock/types').Booking | null} confirmation
 */

/** @type {BookingState} */
const emptyState = {
  searchCriteria: null,
  selectedFlight: null,
  passengers: [],
  contact: null,
  fare: null,
  confirmation: null,
};

const loadInitial = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? { ...emptyState, ...JSON.parse(saved) } : emptyState;
  } catch {
    return emptyState;
  }
};

const BookingContext = createContext(null);

export function BookingProvider({ children }) {
  const [state, setState] = useState(loadInitial);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* ignore storage errors (quota / privacy mode) */
    }
  }, [state]);

  /** Shallow-merge a partial update into the booking state. */
  const update = (partial) => setState((prev) => ({ ...prev, ...partial }));

  const setSearchCriteria = (searchCriteria) => update({ searchCriteria });
  const setSelectedFlight = (selectedFlight) => update({ selectedFlight });
  const setPassengers = (passengers) => update({ passengers });
  const setContact = (contact) => update({ contact });
  const setFare = (fare) => update({ fare });
  const setConfirmation = (confirmation) => update({ confirmation });

  /** Reset the whole flow (e.g. after "Back to Home" on confirmation). */
  const resetBooking = () => {
    setState(emptyState);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  const value = {
    ...state,
    update,
    setSearchCriteria,
    setSelectedFlight,
    setPassengers,
    setContact,
    setFare,
    setConfirmation,
    resetBooking,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

/** Access the booking flow state + updaters. Must be used within <BookingProvider>. */
export function useBooking() {
  const ctx = useContext(BookingContext);
  if (!ctx) {
    throw new Error("useBooking must be used within a <BookingProvider>.");
  }
  return ctx;
}

export default BookingContext;
